import React, { useEffect, useRef } from 'react';
import qrcode from 'qrcode-generator';

interface QRCodeWithLogoProps {
  url: string;
  logo: string;
  size?: number;
  className?: string;
  alt?: string;
}

const QRCodeWithLogo: React.FC<QRCodeWithLogoProps> = ({
  url,
  logo,
  size = 200,
  className = '',
  alt = 'QR Code'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const generateQRCodeWithLogo = () => {
      if (!canvasRef.current) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      try {
        // Generate QR code
        const qr = qrcode(0, 'M');
        qr.addData(url);
        qr.make();

        // Get QR code matrix
        const moduleCount = qr.getModuleCount();
        const moduleSize = size / moduleCount;
        
        // Calculate logo area in modules
        const logoSizeInModules = Math.floor(moduleCount * 0.25); // 25% of QR code
        const logoStartModule = Math.floor((moduleCount - logoSizeInModules) / 2);
        const logoEndModule = logoStartModule + logoSizeInModules;

        // Clear canvas
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, size, size);

        // Draw QR code modules
        ctx.fillStyle = '#000000';
        for (let row = 0; row < moduleCount; row++) {
          for (let col = 0; col < moduleCount; col++) {
            // Skip modules in the logo area
            if (row >= logoStartModule && row < logoEndModule && 
                col >= logoStartModule && col < logoEndModule) {
              continue;
            }

            if (qr.isDark(row, col)) {
              const x = col * moduleSize;
              const y = row * moduleSize;
              ctx.fillRect(x, y, moduleSize, moduleSize);
            }
          }
        }

        // Load and draw logo
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.onload = () => {
          const logoSize = logoSizeInModules * moduleSize;
          const logoX = logoStartModule * moduleSize;
          const logoY = logoStartModule * moduleSize;

          // Create circular background for logo
          ctx.save();
          ctx.beginPath();
          ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, 2 * Math.PI);
          ctx.fillStyle = '#FFFFFF';
          ctx.fill();
          ctx.restore();

          // Draw logo
          ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
        };
        logoImg.src = logo;

      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCodeWithLogo();
  }, [url, logo, size]);

  return (
    <div className={className} title={alt}>
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
      />
    </div>
  );
};

export default QRCodeWithLogo;
